/**
 * 远程模板接口不可用时的持久化兜底。
 * 这里只保存已经由 PDF 服务落盘后的模板元数据，不保存 File/Blob。
 */

const PENDING_TEMPLATE_KEY = 'cloud_design_platform_pending_templates';

function storageAvailable() {
  return typeof localStorage !== 'undefined';
}

function normalizePendingTemplate(template = {}) {
  return {
    ...template,
    id: String(template.id || `t_pdf_pending_${Date.now()}`),
    pendingPersistence: true,
  };
}

export function loadPendingTemplates(storeId) {
  if (!storageAvailable()) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_TEMPLATE_KEY) || '[]');
    const templates = Array.isArray(parsed)
      ? parsed.filter(item => item?.id).map(normalizePendingTemplate)
      : [];
    if (!storeId || storeId === 'all') return templates;
    return templates.filter(item => item.storeId === storeId);
  } catch {
    return [];
  }
}

export function savePendingTemplate(template) {
  if (!storageAvailable() || !template?.id) return null;
  const next = normalizePendingTemplate(template);
  const templates = loadPendingTemplates().filter(item => String(item.id) !== next.id);
  templates.unshift(next);
  try {
    localStorage.setItem(PENDING_TEMPLATE_KEY, JSON.stringify(templates));
  } catch {
    const slim = { ...next };
    if (typeof slim.layerJson === 'string' && slim.layerJson.length > 200_000) {
      delete slim.layerJson;
      slim.layersRecoveryHint = 'originPdf';
    }
    const slimTemplates = templates.map(item =>
      String(item.id) === String(slim.id) ? slim : item
    );
    try {
      localStorage.setItem(PENDING_TEMPLATE_KEY, JSON.stringify(slimTemplates));
      return slim;
    } catch {
      return next;
    }
  }
  return next;
}

export function removePendingTemplate(templateId) {
  if (!storageAvailable() || templateId == null) return;
  const id = String(templateId);
  const templates = loadPendingTemplates().filter(item => String(item.id) !== id);
  if (templates.length) {
    localStorage.setItem(PENDING_TEMPLATE_KEY, JSON.stringify(templates));
  } else {
    localStorage.removeItem(PENDING_TEMPLATE_KEY);
  }
}

export function isPendingTemplate(template) {
  if (!template) return false;
  const id = String(template.id || '');
  return template.pendingPersistence === true
    || id.startsWith('t_pdf_')
    || id.startsWith('t_import_');
}

function templateAssetFingerprint(value) {
  return String(value || '').trim().split('?')[0];
}

export function pendingTemplateMatchesRemote(pending = {}, remote = {}) {
  const pendingOrigin = templateAssetFingerprint(pending.originFileUrl);
  const remoteOrigin = templateAssetFingerprint(remote.originFileUrl);
  if (pendingOrigin && remoteOrigin && pendingOrigin === remoteOrigin) return true;
  const pendingThumb = templateAssetFingerprint(pending.thumb || pending.coverThumbUrl);
  const remoteThumb = templateAssetFingerprint(remote.thumb || remote.coverThumbUrl);
  return Boolean(
    pending.name
    && remote.name
    && pending.name === remote.name
    && pendingThumb
    && pendingThumb === remoteThumb
  );
}

export function dedupeTemplatesById(list = []) {
  const seen = new Set();
  return (list || []).filter((item) => {
    const id = String(item?.id || '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** 远程列表与本地待同步记录按 id / 源文件合并，避免同一模版出现「原件 + 待保存」两张卡片 */
export function mergeRemoteTemplatesWithPending(remoteList = [], pendingList = []) {
  const pendingById = new Map();
  (pendingList || []).forEach((item) => {
    if (!item?.id) return;
    const key = String(item.id);
    pendingById.set(key, { ...pendingById.get(key), ...item });
  });
  const consumedPendingIds = new Set();
  const pendingPool = [...pendingById.values()];
  const seenRemoteIds = new Set();
  const hydratedRemote = [];

  (remoteList || []).forEach((item) => {
    if (!item?.id) return;
    const exactId = String(item.id);
    if (seenRemoteIds.has(exactId)) return;
    seenRemoteIds.add(exactId);
    const pending = pendingById.get(exactId)
      || pendingPool.find(candidate => (
        !consumedPendingIds.has(String(candidate.id))
        && pendingTemplateMatchesRemote(candidate, item)
      ));
    if (!pending) {
      hydratedRemote.push(item);
      return;
    }
    consumedPendingIds.add(String(pending.id));
    hydratedRemote.push({
      ...item,
      layerJson: pending.layerJson || item.layerJson,
      importStats: pending.importStats,
      recoveredFromSource: pending.recoveredFromSource,
      pendingPersistence: pending.pendingPersistence,
    });
  });

  const unmatchedPending = [];
  pendingById.forEach((pending, id) => {
    if (!consumedPendingIds.has(id) && !seenRemoteIds.has(id)) {
      unmatchedPending.push(pending);
    }
  });
  return [...unmatchedPending, ...hydratedRemote];
}

export { PENDING_TEMPLATE_KEY };
